import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { ButtonBack } from '../MediaPanel/styles';
import ResourceSelector from '../../../../containers/ResourceSelector';
import MainView from '../MediaPanel/MainView';
import Utils from '../../../../utils';
import { Api } from '../../../../typings/api';
import { removeFileFromImport } from '../../../../containers/ImportManager/actions';
import { EditableType } from '../../../../containers/Editor/constants';
import { setPageBackground } from '../../../../containers/Editor/actions';
import { BackgroundMediaData } from '../../../../containers/Editor/reducer';

interface PageBackgroundPanelProps {
	type: EditableType;
	page: number;
	currentBgMediaData: BackgroundMediaData | undefined;
	isDefaultBgMediaData: boolean;
	isGeneralBgData: boolean;
	importManagerId: string;
	isImporting: boolean;
	dispatch: Dispatch<StoreState>;
}

interface ConfigItem {
	isValidResourceFromApp: (item: Api.DriveItem) => boolean;
	fileExtensions: string[];
}

const config: { [k: string]: ConfigItem } = {
	[EditableType.image]: {
		isValidResourceFromApp: ({ type }) => type === Api.DriveItemType.Image,
		fileExtensions: ['.jpg', '.jpeg', '.png', '.svg'],
	},
	[EditableType.video]: {
		isValidResourceFromApp: ({ type }) => type === Api.DriveItemType.Movie,
		fileExtensions: ['.mp4', '.webm', '.avi', '.mov'],
	},
};

let isMounted = false;

const PageBackgroundPanel = ({
	type,
	page,
	currentBgMediaData,
	isDefaultBgMediaData,
	importManagerId,
	isImporting,
	isGeneralBgData,
	dispatch,
}: PageBackgroundPanelProps) => {
	const { isValidResourceFromApp, fileExtensions } = config[type];

	const [isResourceSelectorDisplayed, setIsResourceSelectorDisplayed] = React.useState(isImporting);

	const handleResourceSelect = (driveItem: Api.DriveItem): void => {
		const { id, label, src, type, status } = driveItem;
		const aspDriveType = type === Api.DriveItemType.Image
			? Api.AssetItemFileTypes.IMAGE
			: Api.AssetItemFileTypes.MOVIE;

		dispatch(setPageBackground(page, {
			globalId: id,
			label,
			relUrl: src,
			type: aspDriveType,
		}, isGeneralBgData));

		const isProcessing = status && status !== Api.AssetItemStatus.LIVE;

		// do not update state on unmounted component to avoid memory leaks
		if (isMounted && !isProcessing) {
			setIsResourceSelectorDisplayed(false);
		}
	};

	const handleResourceRemove = (): void => {
		if (isImporting) {
			dispatch(removeFileFromImport(importManagerId));
		}

		dispatch(setPageBackground(page, null, isGeneralBgData));
	};

	React.useEffect(() => {
		isMounted = true;
		return () => {
			isMounted = false;
		};
	}, []);

	React.useEffect(() => {
		setIsResourceSelectorDisplayed(isImporting);
	}, [isImporting]);

	const mediaType = currentBgMediaData ? currentBgMediaData.type : Api.AssetItemFileTypes.IMAGE;
	const label = currentBgMediaData ? currentBgMediaData.label : '';

	const iconName = mediaType === Api.AssetItemFileTypes.IMAGE
		? 'iconset_uprez_image'
		: 'iconset_uprez_video';

	return (
		<>
			{!isResourceSelectorDisplayed
				? (
					<MainView
						iconName={iconName}
						defaultLabel=""
						label={label}
						externalLink=""
						isExternalLinkActive={false}
						isResetButtonDisplayed={isDefaultBgMediaData}
						onReplaceClick={() => setIsResourceSelectorDisplayed(true)}
						onResetClick={handleResourceRemove}
						onExternalLinkChange={() => undefined}
					/>
				)
				: (
					<>
						<ButtonBack
							type="button"
							onClick={() => setIsResourceSelectorDisplayed(false)}
						>
							{Utils.getSvgIcon('iconset_back')}
							<Translate value="messages.back" />
						</ButtonBack>
						<ResourceSelector
							id={importManagerId}
							label="messages.replace_file"
							isValidResourceFromApp={isValidResourceFromApp}
							fileExtensions={fileExtensions}
							isFileExtensionsDisplayed={true}
							onSelect={handleResourceSelect}
						/>
					</>
				)
			}
		</>
	);
};

export default PageBackgroundPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/PageBackgroundPanel/index.tsx